import React, { useState } from "react";
import { Box, Typography, Collapse, Chip, Stack } from "@mui/material";
import {
  MapPin,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  Mail,
  Home,
  Key,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { AddressDetailsForm } from "./forms/AddressDetailsForm";
import { ContactInfoForm } from "./forms/ContactInfoForm";
import { PickupTimeForm } from "./forms/PickupTimeForm";
import { SectionHeader, formatTimeRange } from "./SectionHeader";

export const PickupSection = ({
  address,
  // Time
  pickupTime,
  setPickupTime,
  latestPickUpTime,
  setLatestPickUpTime,

  hasTimeWindow,
  setHasTimeWindow,
  // Address details
  apartmentNumber,
  setApartmentNumber,
  floor,
  setFloor,
  buildingName,
  setBuildingName,
  accessCode,
  setAccessCode,
  instructions,
  setInstructions,
  // Contact
  name,
  setName,
  phone,
  setPhone,
  email,
  setEmail,
  inModal = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasAddressDetails =
    apartmentNumber || floor || buildingName || accessCode || instructions;
  const hasContactDetails = name || phone || email;
  const hasAnyDetails = hasAddressDetails || hasContactDetails;

  const summaryItems = [];
  if (name) summaryItems.push({ icon: <User size={12} />, label: name });
  if (phone) summaryItems.push({ icon: <Phone size={12} />, label: phone });
  if (email) summaryItems.push({ icon: <Mail size={12} />, label: email });
  if (apartmentNumber)
    summaryItems.push({
      icon: <Home size={12} />,
      label: `${t("delivery.form.apartmentNumber", "Apt")} ${apartmentNumber}`,
    });
  if (floor)
    summaryItems.push({
      icon: <Home size={12} />,
      label: `${t("delivery.form.floor", "Floor")} ${floor}`,
    });
  if (buildingName)
    summaryItems.push({ icon: <Home size={12} />, label: buildingName });
  if (accessCode)
    summaryItems.push({
      icon: <Key size={12} />,
      label: `${t("delivery.form.accessCode", "Code")}: ${accessCode}`,
    });
  if (instructions)
    summaryItems.push({
      icon: null,
      label: `"${
        instructions.length > 30
          ? instructions.slice(0, 30) + "…"
          : instructions
      }"`,
    });

  return (
    <Box>
      {/* Header */}
      <SectionHeader
        icon={<MapPin size={16} />}
        title={t("delivery.pickupDetails") || "Pickup Details"}
        subtitle={address}
        timeDisplay={formatTimeRange(
          pickupTime,
          hasTimeWindow ? latestPickUpTime : null
        )}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        inModal={inModal}
      />

      {/* Collapsed chips */}
      {!expanded && hasAnyDetails && (
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 0.5, pb: 1 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {summaryItems.map((item, idx) => (
              <Chip
                key={idx}
                icon={item.icon || undefined}
                label={item.label}
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  "& .MuiChip-icon": { marginLeft: "6px" },
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 1.5 }}>
          {/* Time */}

          <Box sx={{ mb: 3 }}>
            <PickupTimeForm
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
              latestPickUpTime={latestPickUpTime}
              setLatestPickUpTime={setLatestPickUpTime}
              hasTimeWindow={hasTimeWindow}
              setHasTimeWindow={setHasTimeWindow}
            />
          </Box>

          {/* Address Details */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 1.5,
              display: "block",
            }}
          >
            {t("delivery.addressDetails") || "Address Details"}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <AddressDetailsForm
              apartmentNumber={apartmentNumber}
              setApartmentNumber={setApartmentNumber}
              floor={floor}
              setFloor={setFloor}
              buildingName={buildingName}
              setBuildingName={setBuildingName}
              accessCode={accessCode}
              setAccessCode={setAccessCode}
              instructions={instructions}
              setInstructions={setInstructions}
              isPickup={true}
            />
          </Box>

          {/* Sender Info */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 1.5,
              display: "block",
            }}
          >
            {t("delivery.senderInformation") || "Sender Information"}
          </Typography>
          <Box>
            <ContactInfoForm
              name={name}
              setName={setName}
              phone={phone}
              setPhone={setPhone}
              email={email}
              setEmail={setEmail}
              isSender={true}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
